import { createJurisdictionHeaders } from '@/data';
import { URLS } from '@/data/config';
import { Product } from '@/hooks/use-jurisdiction';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

export type AccountSummaryProductStats = {
    amount_played: number;
    amount_played_bonus: number;
    amount_won: number;
    number_of_bets: number;
};

export type AccountSummaryData = Partial<Record<Product, AccountSummaryProductStats>>;

type PlayHistoryApiStats = AccountSummaryProductStats & {
    from_year: number;
    from_month: number;
    to_year: number;
    to_month: number;
};

type Wallet = {
    total: string;
    real_amount: string;
    free_amount: string;
    bonus_amount: string;
    real_currency: string;
};

type PlayHistoryResponse = {
    data: Record<string, PlayHistoryApiStats>;
    wallet: Wallet;
    fantasy_wallet: Wallet;
    sweeps_cash_wallet: Wallet;
    sweeps_coin_wallet: Wallet;
};

export type PlayHistoryRange = {
    fromYear: number;
    fromMonth: number;
    toYear: number;
    toMonth: number;
};

// Maps the API's product keys to the app's Product enum
const API_PRODUCT_TO_PRODUCT_MAP: Record<string, Product> = {
    FANTASY: Product.Pickem,
    ARCADE: Product.Arcade,
    SWEEPS_CASH_SBK: Product.Sweeps,
    SWEEPS_CASH_CASINO: Product.Casino,
};

const getPlayHistory = async ({
    fromYear,
    fromMonth,
    toYear,
    toMonth,
}: PlayHistoryRange): Promise<PlayHistoryResponse> => {
    const params = new URLSearchParams({
        from_year: `${fromYear}`,
        from_month: `${fromMonth}`,
        to_year: `${toYear}`,
        to_month: `${toMonth}`,
    });
    const resp = await fetch(`${URLS.CHAMELON_API_URL}/auth/wallet/play-history?${params}`, {
        method: 'GET',
        headers: createJurisdictionHeaders(),
    });
    const data = await resp.json();
    if (!resp.ok) {
        throw data;
    }
    return data;
};

const toAccountSummaryData = (response: PlayHistoryResponse): AccountSummaryData => {
    return Object.entries(response.data ?? {}).reduce<AccountSummaryData>((acc, [apiProduct, stats]) => {
        const product = API_PRODUCT_TO_PRODUCT_MAP[apiProduct];
        if (product) {
            acc[product] = {
                amount_played: stats.amount_played,
                amount_played_bonus: stats.amount_played_bonus,
                amount_won: stats.amount_won,
                number_of_bets: stats.number_of_bets,
            };
        }
        return acc;
    }, {});
};

/**
 * Returns the play history stats per product for the given range.
 * Defaults to the current calendar year.
 * */
export const usePlayHistory = (range?: Partial<PlayHistoryRange>) => {
    const currentYear = new Date().getFullYear();
    const params: PlayHistoryRange = {
        fromYear: range?.fromYear ?? currentYear,
        fromMonth: range?.fromMonth ?? 1,
        toYear: range?.toYear ?? currentYear,
        toMonth: range?.toMonth ?? 12,
    };

    const { data = {}, ...query } = useQuery({
        queryKey: ['play-history', params],
        queryFn: () => getPlayHistory(params),
        select: toAccountSummaryData,
        placeholderData: keepPreviousData,
    });

    return { data, ...query };
};
